import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { companyAPI, brainyAPI, publicAPI, myAIPreferencesAPI, llmConfigAPI, aiUsageAPI } from '../api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowLeft, Building2, Users, Shield, Key, Save, Loader2, Plus,
  Trash2, Mail, Copy, CheckCircle2, XCircle, ChevronDown, AlertTriangle,
  UserPlus, Crown, Eye, Wrench, Globe, Bot, Search, X, Settings2,
  Check, Sparkles, MessageSquare, Monitor, Download, Smartphone, Zap
} from 'lucide-react';

const PERM_LABELS = {
  can_view_settings: { label: 'Can View Settings', desc: 'Access company settings and configuration', icon: Settings2 },
  can_add_tools: { label: 'Can Add Tools', desc: 'Create and manage custom tools', icon: Wrench },
  can_search_external: { label: 'Can Search External', desc: 'Use External Knowledge web search mode', icon: Globe },
  can_chat_with_agents: { label: 'Can Chat with AI Agents', desc: 'Use AI Agents chat mode', icon: Bot },
  can_use_brainy: { label: 'Can Use Brainy', desc: 'Access Brainy personal assistant mode', icon: Sparkles },
  can_use_advanced: { label: 'Can Use Advanced Tools', desc: 'Access the Advanced Tools page', icon: Zap },
  can_use_doc_comparison: { label: 'Document Comparison', desc: 'Compare documents side-by-side with AI', icon: Eye },
  can_use_workflows: { label: 'Workflows', desc: 'Create and run workflow automations', icon: Wrench },
  can_use_knowledge_graph: { label: 'Knowledge Graph', desc: 'Generate knowledge graph visualizations', icon: Globe },
  can_use_multi_language: { label: 'Multi-Language', desc: 'Detect and translate document languages', icon: Globe },
  can_use_scheduled_agents: { label: 'Scheduled Agents', desc: 'Schedule automated agent runs', icon: Crown },
};

const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Legal', 'Education', 'Government', 'Manufacturing', 'Retail', 'Consulting', 'Other'];

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('company');
  const [deployment, setDeployment] = useState({ is_on_premise: false });

  useEffect(() => {
    document.title = 'Settings | BrainShift AI Workspace';
    loadCompany();
    publicAPI.getDeploymentInfo().then(r => setDeployment(r.data || {})).catch(() => {});
  }, []);

  const loadCompany = async () => {
    try {
      const res = await companyAPI.get();
      setCompany(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <SettingsLoader />;

  const canViewSettings = company?.my_permissions?.can_view_settings !== false;
  if (!canViewSettings) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#111827] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Access Restricted</h2>
          <p className="text-sm text-[#6B7280] mb-4">You don't have permission to view settings.</p>
          <button onClick={() => navigate('/dashboard')} className="h-9 px-4 bg-[#6590FF] text-white text-sm font-semibold rounded-xl" data-testid="back-to-dashboard-btn">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="settings-page">
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" data-testid="settings-back-btn">
            <ArrowLeft className="w-4 h-4 text-[#4B5563]" />
          </button>
          <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto hidden sm:block" style={{ height: '1.5rem' }} />
          <h1 className="text-base font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Settings</h1>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[#6B7280]">{user?.first_name} {user?.last_name}</span>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{company?.my_role_name}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {(() => {
          const isOnpremGate = deployment.is_on_premise && !user?.is_super_admin && !sessionStorage.getItem('bkw_acting_tenant_id');
          if (isOnpremGate) {
            return (
              <div className="mb-6 p-6 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl flex items-start gap-3" data-testid="onprem-banner">
                <Shield className="w-6 h-6 text-[#D97706] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-[#78350F] mb-1">Settings managed by your platform administrator</h3>
                  <p className="text-xs text-[#92400E] leading-relaxed">
                    Your installation is running in <strong>On-Premise mode</strong>. Company info, Users &amp; Invites, Roles &amp; Permissions, SSO and Tool Visibility settings are managed centrally by your SuperAdmin. Please reach out to your administrator for any changes.
                  </p>
                </div>
              </div>
            );
          }
          return (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-[#F3F4F6] p-1 rounded-xl mb-8 flex flex-wrap gap-0" data-testid="settings-tabs">
                <TabsTrigger value="company" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-company">
                  <Building2 className="w-3.5 h-3.5" /> Company
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-users">
                  <Users className="w-3.5 h-3.5" /> Users & Invites
                </TabsTrigger>
                <TabsTrigger value="roles" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-roles">
                  <Shield className="w-3.5 h-3.5" /> Roles & Permissions
                </TabsTrigger>
                <TabsTrigger value="sso" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-sso">
                  <Key className="w-3.5 h-3.5" /> SSO Config
                </TabsTrigger>
                <TabsTrigger value="brainy" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-brainy">
                  <Sparkles className="w-3.5 h-3.5" /> Brainy
                </TabsTrigger>
                <TabsTrigger value="tools-vis" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-tools-vis">
                  <Wrench className="w-3.5 h-3.5" /> Tool Visibility
                </TabsTrigger>
                <TabsTrigger value="my-ai" className="rounded-lg text-xs font-semibold gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#111827] data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-my-ai">
                  <Bot className="w-3.5 h-3.5" /> My AI
                </TabsTrigger>
              </TabsList>
              <TabsContent value="company"><CompanyTab company={company} onReload={loadCompany} /></TabsContent>
              <TabsContent value="users"><UsersTab company={company} /></TabsContent>
              <TabsContent value="roles"><RolesTab company={company} /></TabsContent>
              <TabsContent value="sso"><SSOTab /></TabsContent>
              <TabsContent value="brainy"><BrainyTab /></TabsContent>
              <TabsContent value="tools-vis"><ToolVisibilityTab /></TabsContent>
              <TabsContent value="my-ai"><MyAIDefaultTab /></TabsContent>
            </Tabs>
          );
        })()}
      </main>
    </div>
  );
}

function SettingsLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex items-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" />
        <span className="text-sm text-[#6B7280]">Loading settings...</span>
      </div>
    </div>
  );
}

// ---- Company Info Tab ----
function CompanyTab({ company, onReload }) {
  const [form, setForm] = useState({
    name: company?.name || '',
    domain: company?.domain || '',
    logo_url: company?.logo_url || '',
    address: company?.address || '',
    phone: company?.phone || '',
    industry: company?.industry || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAPI.update(form);
      setSaved(true);
      onReload();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="company-tab">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Company Information</h2>
        <p className="text-sm text-[#6B7280]">Manage your organization's details.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Company Name</Label>
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="Acme Corp" data-testid="company-name-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Domain</Label>
          <Input value={form.domain} onChange={e => setForm({...form, domain: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="acme.com" data-testid="company-domain-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Phone</Label>
          <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="+1 (555) 000-0000" data-testid="company-phone-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Industry</Label>
          <div className="relative">
            <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})}
              className="w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6590FF]/20 focus:border-[#6590FF]"
              data-testid="company-industry-select">
              <option value="">Select industry...</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Address</Label>
          <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="123 Business St, Suite 100, City, State" data-testid="company-address-input" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Logo URL</Label>
          <Input value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="https://example.com/logo.png" data-testid="company-logo-input" />
          {form.logo_url && (
            <div className="mt-2 flex items-center gap-2">
              <img src={form.logo_url} alt="Logo preview" className="w-10 h-10 rounded-lg object-contain border border-[#E5E7EB]" onError={e => e.target.style.display='none'} />
              <span className="text-xs text-[#9CA3AF]">Preview</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={handleSave} disabled={saving}
          className="h-10 px-6 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="save-company-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ---- Users & Invites Tab ----
function UsersTab({ company }) {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersRes, invitesRes, rolesRes] = await Promise.all([
        companyAPI.listUsers(),
        companyAPI.listInvites(),
        companyAPI.listRoles(),
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setRoles(rolesRes.data);
      if (!inviteRoleId && rolesRes.data.length) {
        const staffRole = rolesRes.data.find(r => r.name === 'Staff User');
        setInviteRoleId(staffRole?.id || rolesRes.data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteRoleId) return;
    setInviting(true);
    try {
      const res = await companyAPI.invite({ email: inviteEmail, role_id: inviteRoleId });
      setInviteResult(res.data);
      setInviteEmail('');
      loadAll();
    } catch (e) {
      setInviteResult({ error: e.response?.data?.detail || 'Failed to send invite' });
    }
    finally { setInviting(false); }
  };

  const handleChangeRole = async (userId, roleId) => {
    try {
      await companyAPI.changeUserRole(userId, { role_id: roleId });
      loadAll();
    } catch (e) { console.error(e); }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user from the company?')) return;
    try {
      await companyAPI.removeUser(userId);
      loadAll();
    } catch (e) { console.error(e); }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await companyAPI.cancelInvite(inviteId);
      loadAll();
    } catch (e) { console.error(e); }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/register?invite=${token}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" /><span className="text-sm text-[#6B7280]">Loading...</span></div>;

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div className="space-y-8" data-testid="users-tab">
      {/* Invite Section */}
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Invite Users</h2>
        <p className="text-sm text-[#6B7280] mb-4">Send an email invitation to add team members.</p>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com" className="rounded-xl border-[#E5E7EB] h-10" data-testid="invite-email-input" />
          </div>
          <div className="w-full sm:w-48">
            <div className="relative">
              <select value={inviteRoleId} onChange={e => setInviteRoleId(e.target.value)}
                className="w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6590FF]/20"
                data-testid="invite-role-select">
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
            </div>
          </div>
          <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}
            className="h-10 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            data-testid="send-invite-btn">
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Send Invite
          </button>
        </div>

        {inviteResult && (
          <div className={`mt-3 p-3 rounded-xl border ${inviteResult.error ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid="invite-result">
            {inviteResult.error ? (
              <p className="text-sm text-red-700 flex items-center gap-2"><XCircle className="w-4 h-4" />{inviteResult.error}</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Invite sent to {inviteResult.email}</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white px-2 py-1 rounded-lg border border-emerald-200 font-mono flex-1 truncate">
                    {window.location.origin}/register?invite={inviteResult.token}
                  </code>
                  <button onClick={() => copyInviteLink(inviteResult.token)} className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors" data-testid="copy-invite-link">
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-emerald-600" />}
                  </button>
                </div>
                <p className="text-[11px] text-emerald-600">Share this link with the user to accept the invitation.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Mail className="w-4 h-4 text-[#D97706]" /> Pending Invites
          </h3>
          <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#F3F4F6] overflow-hidden">
            {pendingInvites.map(inv => (
              <div key={inv.id} className="px-4 py-3 flex items-center gap-3" data-testid={`invite-row-${inv.email}`}>
                <div className="w-8 h-8 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-3.5 h-3.5 text-[#D97706]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#111827] truncate">{inv.email}</p>
                  <p className="text-[11px] text-[#9CA3AF]">Role: {inv.role_name} &middot; Invited by {inv.invited_by}</p>
                </div>
                <button onClick={() => copyInviteLink(inv.token)} className="p-1.5 hover:bg-[#F3F4F6] rounded-lg" title="Copy invite link"><Copy className="w-3.5 h-3.5 text-[#6B7280]" /></button>
                <button onClick={() => handleCancelInvite(inv.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Cancel invite" data-testid={`cancel-invite-${inv.email}`}><X className="w-3.5 h-3.5 text-[#DC2626]" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Users */}
      <div>
        <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Users className="w-4 h-4 text-[#6590FF]" /> Team Members ({users.length})
        </h3>
        <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#F3F4F6] overflow-hidden">
          {users.map(u => (
            <div key={u.id} className="px-4 py-3 flex items-center gap-3" data-testid={`user-row-${u.email}`}>
              <div className="w-8 h-8 rounded-full bg-[#6590FF]/5 flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#6590FF]">
                {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827] truncate">{u.first_name} {u.last_name}</p>
                <p className="text-[11px] text-[#9CA3AF]">{u.email}</p>
              </div>
              <div className="w-40 flex-shrink-0">
                <div className="relative">
                  <select value={u.role_id} onChange={e => handleChangeRole(u.user_id, e.target.value)}
                    className="w-full h-8 rounded-lg border border-[#E5E7EB] px-2 text-xs bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6590FF]/20"
                    data-testid={`user-role-select-${u.email}`}>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#9CA3AF] pointer-events-none" />
                </div>
              </div>
              {u.role_name === 'Admin' && <Crown className="w-4 h-4 text-[#D97706] flex-shrink-0" title="Admin" />}
              <button onClick={() => handleRemoveUser(u.user_id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Remove user" data-testid={`remove-user-${u.email}`}>
                <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Roles & Permissions Tab ----
function RolesTab({ company }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [agents, setAgents] = useState([]);

  const BUILTIN_AGENTS = [
    { id: 'compliance', name: 'Compliance Review' },
    { id: 'conflict', name: 'Conflict Detection' },
    { id: 'recommendation', name: 'Recommendations' },
  ];

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const res = await companyAPI.listRoles();
      setRoles(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    setCreating(true);
    try {
      await companyAPI.createRole({
        name: newRoleName,
        permissions: Object.fromEntries(Object.keys(PERM_LABELS).map(k => [k, true])),
        agent_ids: BUILTIN_AGENTS.map(a => a.id),
      });
      setNewRoleName('');
      loadRoles();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleTogglePerm = async (roleId, permKey, currentValue) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;
    const updated = { ...role.permissions, [permKey]: !currentValue };
    try {
      await companyAPI.updateRole(roleId, { permissions: updated });
      loadRoles();
    } catch (e) { console.error(e); }
  };

  const handleToggleAgent = async (roleId, agentId, currentAgentIds) => {
    const has = currentAgentIds.includes(agentId);
    const updated = has ? currentAgentIds.filter(a => a !== agentId) : [...currentAgentIds, agentId];
    try {
      await companyAPI.updateRole(roleId, { agent_ids: updated });
      loadRoles();
    } catch (e) { console.error(e); }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this custom role?')) return;
    try {
      await companyAPI.deleteRole(roleId);
      loadRoles();
    } catch (e) { alert(e.response?.data?.detail || 'Cannot delete role'); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" /><span className="text-sm text-[#6B7280]">Loading...</span></div>;

  return (
    <div className="space-y-8" data-testid="roles-tab">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Roles & Permissions</h2>
        <p className="text-sm text-[#6B7280]">Manage roles and what each role can access.</p>
      </div>

      {/* Create custom role */}
      <div className="flex items-center gap-3">
        <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="New role name..." className="rounded-xl border-[#E5E7EB] h-10 max-w-xs" data-testid="new-role-name-input"
          onKeyDown={e => { if (e.key === 'Enter') handleCreateRole(); }} />
        <button onClick={handleCreateRole} disabled={creating || !newRoleName.trim()}
          className="h-10 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="create-role-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create Role
        </button>
      </div>

      {/* Roles list */}
      <div className="space-y-4">
        {roles.map(role => (
          <div key={role.id} className="border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={`role-card-${role.name}`}>
            <div className="px-5 py-3 bg-[#F9FAFB] flex items-center gap-3">
              <Shield className="w-4 h-4 text-[#6590FF]" />
              <span className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>{role.name}</span>
              {role.is_builtin && <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-[#6B7280]">Built-in</Badge>}
              <span className="text-[11px] text-[#9CA3AF] ml-auto">{role.user_count} user{role.user_count !== 1 ? 's' : ''}</span>
              {!role.is_builtin && (
                <button onClick={() => handleDeleteRole(role.id)} className="p-1 hover:bg-red-50 rounded-lg" data-testid={`delete-role-${role.name}`}>
                  <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                </button>
              )}
            </div>

            {/* Permissions */}
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563]">Feature Permissions</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(PERM_LABELS).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <div key={key} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-xl border border-[#F3F4F6]">
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-[#4B5563]" />
                        <div>
                          <p className="text-xs font-medium text-[#111827]">{meta.label}</p>
                          <p className="text-[10px] text-[#9CA3AF]">{meta.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={role.permissions[key] !== false}
                        onCheckedChange={() => handleTogglePerm(role.id, key, role.permissions[key] !== false)}
                        data-testid={`perm-switch-${role.name}-${key}`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Agent Access */}
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] pt-2">AI Agent Visibility</p>
              <div className="flex flex-wrap gap-2">
                {BUILTIN_AGENTS.map(agent => {
                  const hasAccess = role.agent_ids?.includes(agent.id);
                  return (
                    <button key={agent.id}
                      onClick={() => handleToggleAgent(role.id, agent.id, role.agent_ids || [])}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                        hasAccess
                          ? 'bg-[#6590FF]/5 border-[#6590FF]/30 text-[#6590FF]'
                          : 'bg-white border-[#E5E7EB] text-[#9CA3AF] hover:border-[#D1D5DB]'
                      }`}
                      data-testid={`agent-toggle-${role.name}-${agent.id}`}>
                      <Bot className="w-3.5 h-3.5" />
                      {agent.name}
                      {hasAccess && <Check className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- SSO Config Tab ----
function SSOTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    provider: '', client_id: '', client_secret: '', tenant_id: '', metadata_url: '', enabled: false,
  });

  useEffect(() => { loadSSO(); }, []);

  const loadSSO = async () => {
    try {
      const res = await companyAPI.getSSO();
      setConfig(res.data);
      setForm({
        provider: res.data.provider || '',
        client_id: res.data.client_id || '',
        client_secret: '',
        tenant_id: res.data.tenant_id || '',
        metadata_url: res.data.metadata_url || '',
        enabled: res.data.enabled || false,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_secret) delete payload.client_secret;
      await companyAPI.updateSSO(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadSSO();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" /><span className="text-sm text-[#6B7280]">Loading...</span></div>;

  const SSO_PROVIDERS = ['Azure AD', 'Okta', 'Google Workspace', 'OneLogin', 'Auth0', 'Custom SAML', 'Custom OIDC'];

  return (
    <div className="space-y-6" data-testid="sso-tab">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>SSO Configuration</h2>
        <p className="text-sm text-[#6B7280]">Configure Single Sign-On for your organization.</p>
      </div>

      <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB]">
        <div>
          <p className="text-sm font-medium text-[#111827]">Enable SSO</p>
          <p className="text-xs text-[#6B7280]">Allow users to sign in with your identity provider</p>
        </div>
        <Switch checked={form.enabled} onCheckedChange={v => setForm({...form, enabled: v})} data-testid="sso-enabled-switch" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">SSO Provider</Label>
          <div className="relative">
            <select value={form.provider} onChange={e => setForm({...form, provider: e.target.value})}
              className="w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6590FF]/20"
              data-testid="sso-provider-select">
              <option value="">Select provider...</option>
              {SSO_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Tenant / Directory ID</Label>
          <Input value={form.tenant_id} onChange={e => setForm({...form, tenant_id: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="e.g., xxxxxxxx-xxxx-xxxx-xxxx" data-testid="sso-tenant-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Client ID</Label>
          <Input value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="Application client ID" data-testid="sso-client-id-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Client Secret</Label>
          <Input type="password" autoComplete="off" name="sso-client-secret" data-1p-ignore="true" data-lpignore="true" value={form.client_secret} onChange={e => setForm({...form, client_secret: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10"
            placeholder={config?.client_secret_masked || 'Enter client secret'} data-testid="sso-client-secret-input" />
          {config?.client_secret_masked && <p className="text-[10px] text-[#9CA3AF] mt-1">Current: {config.client_secret_masked} (leave blank to keep)</p>}
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Metadata URL / Discovery Endpoint</Label>
          <Input value={form.metadata_url} onChange={e => setForm({...form, metadata_url: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10"
            placeholder="https://login.microsoftonline.com/{tenant}/.well-known/openid-configuration" data-testid="sso-metadata-input" />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={handleSave} disabled={saving}
          className="h-10 px-6 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="save-sso-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save SSO Config'}
        </button>
      </div>

      {form.enabled && (
        <div className="p-4 bg-[#FFF7ED] border border-[#FDBA74] rounded-xl flex items-start gap-3" data-testid="sso-warning">
          <AlertTriangle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#92400E]">SSO Integration Note</p>
            <p className="text-xs text-[#92400E]/80 mt-1">
              Once enabled, SSO authentication will be available for users with email domains matching your company domain.
              Ensure your identity provider is properly configured before enabling.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


// ---- Brainy Tab ----
function BrainyTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    enabled: true,
    whatsapp_phone_id: '',
    whatsapp_token: '',
    whatsapp_verify_token: '',
    whatsapp_business_account_id: '',
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const res = await brainyAPI.getConfig();
      setConfig(res.data);
      setForm({
        enabled: res.data.enabled !== false,
        whatsapp_phone_id: res.data.whatsapp_phone_id || '',
        whatsapp_token: '',
        whatsapp_verify_token: '',
        whatsapp_business_account_id: res.data.whatsapp_business_account_id || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.whatsapp_token) delete payload.whatsapp_token;
      if (!payload.whatsapp_verify_token) delete payload.whatsapp_verify_token;
      await brainyAPI.updateConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadConfig();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin text-[#EA580C]" /><span className="text-sm text-[#6B7280]">Loading...</span></div>;

  return (
    <div className="space-y-8" data-testid="brainy-tab">
      {/* Enable/Disable */}
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Brainy Personal Assistant</h2>
        <p className="text-sm text-[#6B7280]">Configure Brainy, your team's AI-powered personal assistant with tasks, reminders, and integrations.</p>
      </div>

      <div className="flex items-center justify-between p-4 bg-[#FFF7ED] rounded-xl border border-[#FED7AA]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EA580C]/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#EA580C]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#111827]">Enable Brainy</p>
            <p className="text-xs text-[#6B7280]">Allow users to access the Brainy personal assistant chat mode</p>
          </div>
        </div>
        <Switch checked={form.enabled} onCheckedChange={v => setForm({...form, enabled: v})} data-testid="brainy-enabled-switch" />
      </div>

      {/* WhatsApp Configuration */}
      <div>
        <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Smartphone className="w-4 h-4 text-[#25D366]" /> WhatsApp Integration
        </h3>
        <p className="text-xs text-[#6B7280] mb-4">Connect WhatsApp Business Cloud API to send task reminders and notifications to users via WhatsApp.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Phone Number ID</Label>
            <Input value={form.whatsapp_phone_id} onChange={e => setForm({...form, whatsapp_phone_id: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="e.g., 115738927..." data-testid="whatsapp-phone-id-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Business Account ID</Label>
            <Input value={form.whatsapp_business_account_id} onChange={e => setForm({...form, whatsapp_business_account_id: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10" placeholder="e.g., 107654..." data-testid="whatsapp-business-id-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Access Token</Label>
            <Input type="password" autoComplete="off" name="whatsapp-token" data-1p-ignore="true" data-lpignore="true" value={form.whatsapp_token} onChange={e => setForm({...form, whatsapp_token: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10"
              placeholder={config?.whatsapp_token_masked || 'Permanent access token'} data-testid="whatsapp-token-input" />
            {config?.whatsapp_token_masked && <p className="text-[10px] text-[#9CA3AF] mt-1">Current: {config.whatsapp_token_masked} (leave blank to keep)</p>}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">Verify Token</Label>
            <Input type="password" autoComplete="off" name="whatsapp-verify-token" data-1p-ignore="true" data-lpignore="true" value={form.whatsapp_verify_token} onChange={e => setForm({...form, whatsapp_verify_token: e.target.value})} className="rounded-xl border-[#E5E7EB] h-10"
              placeholder="Webhook verify token" data-testid="whatsapp-verify-token-input" />
          </div>
        </div>

        {config?.whatsapp_connected && (
          <div className="mt-3 p-3 rounded-xl bg-[#ECFDF5] border border-[#A7F3D0] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#059669]" />
            <span className="text-sm text-[#065F46]">WhatsApp connected</span>
          </div>
        )}
      </div>

      {/* Desktop Client Download */}
      <div>
        <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Monitor className="w-4 h-4 text-[#6590FF]" /> Brainy Desktop Client
        </h3>
        <p className="text-xs text-[#6B7280] mb-4">Download the Brainy desktop client for advanced automation, file management, and system-level tools on your computer.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { os: 'Windows', icon: '⊞', ext: '.exe', desc: 'Windows 10/11 (64-bit)', color: '#0078D4' },
            { os: 'macOS', icon: '⌘', ext: '.dmg', desc: 'macOS 12+ (Apple Silicon & Intel)', color: '#333333' },
            { os: 'Linux', icon: '⌨', ext: '.AppImage', desc: 'Ubuntu 22.04+, Fedora 38+', color: '#E95420' },
          ].map(item => (
            <div key={item.os} className="p-4 border border-[#E5E7EB] rounded-xl hover:border-[#D1D5DB] transition-colors group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: item.color }}>
                  {item.icon}
                </div>
                <div>
                  <p className="text-sm font-bold text-[#111827]">{item.os}</p>
                  <p className="text-[10px] text-[#9CA3AF]">{item.desc}</p>
                </div>
              </div>
              <button className="w-full h-9 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#4B5563] text-xs font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5 group-hover:bg-[#E5E7EB]"
                data-testid={`download-brainy-${item.os.toLowerCase()}`}
                onClick={() => alert(`Brainy Desktop for ${item.os} — Coming soon! The download will be available in the next release.`)}>
                <Download className="w-3.5 h-3.5" />
                Download {item.ext}
              </button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#9CA3AF] mt-2 text-center">Desktop client enables local file processing, system automation, and offline task management.</p>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={handleSave} disabled={saving}
          className="h-10 px-6 bg-[#EA580C] hover:bg-[#C2410C] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="save-brainy-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save Brainy Config'}
        </button>
      </div>

      {form.enabled && (
        <div className="p-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl flex items-start gap-3" data-testid="brainy-info-panel">
          <Sparkles className="w-5 h-5 text-[#EA580C] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#92400E]">Brainy Capabilities</p>
            <ul className="text-xs text-[#92400E]/80 mt-1 space-y-0.5 list-disc list-inside">
              <li>Conversational AI assistant in every workspace</li>
              <li>Natural language task and reminder creation</li>
              <li>WhatsApp notifications for reminders (when connected)</li>
              <li>Desktop automation via Brainy Client (coming soon)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}


// ---- Tool Visibility Tab (Admin controls which tools staff sees) ----
function ToolVisibilityTab() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await companyAPI.getToolVisibility(); setTools(res.data?.tools || []); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggle = async (toolId, currentEnabled) => {
    try {
      await companyAPI.updateToolVisibility(toolId, !currentEnabled);
      load();
    } catch (e) { console.error(e); }
  };

  const groupBy = (arr) => {
    const map = {};
    for (const t of arr) { (map[t.category] = map[t.category] || []).push(t); }
    return Object.entries(map);
  };

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" /><span className="text-sm text-[#6B7280]">Loading...</span></div>;

  return (
    <div className="space-y-6" data-testid="tool-visibility-tab">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Tool Visibility</h2>
        <p className="text-sm text-[#6B7280]">Control which tools are available for users in your workspace. Disabled tools won't appear in the tools panel.</p>
      </div>

      <div className="space-y-4">
        {groupBy(tools).map(([cat, catTools]) => (
          <div key={cat} className="border border-[#E5E7EB] rounded-xl overflow-hidden">
            <div className="px-5 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <span className="text-xs font-bold text-[#4B5563] uppercase tracking-[0.1em]">{cat || 'General'}</span>
            </div>
            <div className="divide-y divide-[#F3F4F6]">
              {catTools.map(t => (
                <div key={t.id} className="px-5 py-3 flex items-center justify-between">
                  <span className="text-sm text-[#111827]">{t.name}</span>
                  <Switch
                    checked={t.enabled}
                    onCheckedChange={() => toggle(t.id, t.enabled)}
                    data-testid={`tool-vis-${t.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---- My AI Default Tab (Phase 1: User-level AI model preference) ----
function MyAIDefaultTab() {
  const [pref, setPref] = useState({ provider: null, model: null, temperature: null, max_tokens: null });
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [prefRes, optsRes] = await Promise.all([
          myAIPreferencesAPI.get(),
          llmConfigAPI.getLLMOptions(),
        ]);
        setPref(prefRes.data || { provider: null, model: null });
        setAllModels(optsRes.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const apply = async (next) => {
    const prev = pref;
    setPref(next);  // optimistic
    setSaving(true);
    try {
      await myAIPreferencesAPI.update(next);
      setSavedTick(true); setTimeout(() => setSavedTick(false), 1500);
    } catch (e) {
      console.error('Save personal default failed:', e);
      setPref(prev);
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const clear = () => apply({ provider: null, model: null, temperature: null, max_tokens: null });
  const isOverriding = !!pref.provider && !!pref.model;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="my-ai-default-tab">
      <div>
        <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>My AI Default</h2>
        <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">
          Pick your personal preferred AI model. It will be used in any workspace where the admin has allowed user overrides — otherwise the workspace default applies.
          Resolution order: <span className="font-mono text-[11px] bg-[#F3F4F6] px-1.5 py-0.5 rounded">Your default → Workspace default → System default</span>
        </p>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#6590FF]/5 flex items-center justify-center"><Bot className="w-5 h-5 text-[#6590FF]" /></div>
          <div className="flex-1">
            <p className="text-sm font-bold text-[#111827]">{isOverriding ? `${pref.provider} / ${pref.model}` : 'Using workspace default'}</p>
            <p className="text-[11px] text-[#6B7280]">{isOverriding ? 'This model is applied wherever user overrides are enabled.' : 'No personal default set.'}</p>
          </div>
          {savedTick && <span className="text-xs font-semibold text-[#059669] flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
        </div>

        <div>
          <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider block mb-2">Choose your default model</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allModels.map(opt => {
              const active = pref.provider === opt.provider && pref.model === opt.model;
              return (
                <button key={`${opt.provider}-${opt.model}`}
                  onClick={() => apply({ ...pref, provider: opt.provider, model: opt.model })}
                  disabled={saving}
                  className={`text-left p-3 rounded-xl border transition-colors ${active ? 'border-[#6590FF] bg-[#6590FF]/5' : 'border-[#E5E7EB] hover:border-[#9CA3AF]'}`}
                  data-testid={`my-ai-option-${opt.model}`}>
                  <div className="text-sm font-semibold text-[#111827]">{opt.label || opt.model}</div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">{opt.provider}</div>
                </button>
              );
            })}
          </div>
        </div>

        {isOverriding && (
          <button onClick={clear} disabled={saving}
            className="h-8 px-3 text-xs font-medium text-[#DC2626] hover:bg-[#FEE2E2] rounded-xl flex items-center gap-1.5"
            data-testid="my-ai-clear-btn">
            <X className="w-3.5 h-3.5" /> Reset to workspace default
          </button>
        )}
      </div>

      <UsageDashboard scope="me" />
    </div>
  );
}


// ---- Usage dashboard (reusable) ----
// scope = 'me' | 'workspace' | 'system'. workspaceId required when scope === 'workspace'.
function UsageDashboard({ scope = 'me', workspaceId = null, companyId = null }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true); setErr(null);
    const p = scope === 'me'
      ? aiUsageAPI.myUsage(days)
      : scope === 'workspace'
        ? aiUsageAPI.workspaceUsage(workspaceId, days)
        : aiUsageAPI.systemUsage(days, companyId);
    p.then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [scope, workspaceId, companyId, days]);

  if (loading) return <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" /></div>;
  if (err) return <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-xl p-4 text-sm text-[#7F1D1D]">{err}</div>;

  const totals = data?.totals || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, cost_usd: 0, calls: 0, errors: 0 };
  const fmt = n => Number(n || 0).toLocaleString();
  const fmtMoney = n => `$${Number(n || 0).toFixed(4)}`;

  return (
    <div className="space-y-4" data-testid={`usage-dashboard-${scope}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-[#111827]">{scope === 'me' ? 'Your usage' : scope === 'workspace' ? 'Workspace usage' : 'System usage'}</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Last {days} days · {fmt(totals.calls)} request{totals.calls === 1 ? '' : 's'}{totals.errors ? ` · ${totals.errors} error${totals.errors === 1 ? '' : 's'}` : ''}</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="h-7 px-2 text-xs border border-[#E5E7EB] rounded-lg bg-white" data-testid="usage-period-select">
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total tokens" value={fmt(totals.total_tokens)} />
        <StatCard label="Prompt tokens" value={fmt(totals.prompt_tokens)} subtle />
        <StatCard label="Completion tokens" value={fmt(totals.completion_tokens)} subtle />
        <StatCard label="Estimated cost" value={fmtMoney(totals.cost_usd)} accent />
      </div>

      {data?.daily?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">Daily token consumption</h4>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data.daily}>
              <defs>
                <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6590FF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#6590FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
              <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, name) => name === 'tokens' ? [fmt(v), 'Tokens'] : [fmtMoney(v), 'Cost']} />
              <Area type="monotone" dataKey="tokens" stroke="#6590FF" fill="url(#usageGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {data?.by_provider?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">By provider · model</h4>
          <div className="space-y-2">
            {data.by_provider.map((row, i) => (
              <div key={`${row.provider || 'unknown'}::${row.model || 'unknown'}`} className="flex items-center justify-between p-2 bg-[#F9FAFB] rounded-lg" data-testid={`usage-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111827] truncate">{row.model}</p>
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">{row.provider}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="font-bold text-[#111827]">{fmt(row.tokens)} tok</p>
                  <p className="text-[#6B7280]">{fmtMoney(row.cost_usd)} · {row.calls} call{row.calls === 1 ? '' : 's'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!data?.daily?.length && !data?.by_provider?.length && (
        <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] rounded-xl p-6 text-center">
          <p className="text-sm text-[#6B7280]">No AI activity in the last {days} days yet.</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Start a chat to see your usage broken down here.</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, subtle = false, accent = false }) {
  const valColor = accent ? 'text-[#6590FF]' : subtle ? 'text-[#4B5563]' : 'text-[#111827]';
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">{label}</p>
      <p className={`text-lg font-bold ${valColor} mt-1`}>{value}</p>
    </div>
  );
}
